import React, { useState, useEffect } from 'react';
import { backupsAPI } from '../services/api';
import './Backups.css';

function Backups() {
  const [backups, setBackups] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    loadBackups();
    loadStats();
  }, []);

  const loadBackups = async () => {
    try {
      setLoading(true);
      const response = await backupsAPI.getAll();
      setBackups(response.data || []);
    } catch (err) {
      setError('Error al cargar los backups');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await backupsAPI.getStats();
      setStats(response.data);
    } catch (err) {
      console.error('Error al cargar estadísticas:', err);
    }
  };

  const handleCreateBackup = async () => {
    try {
      setCreating(true);
      setError(null);
      setSuccess(null);
      
      const response = await backupsAPI.create();
      
      if (response.data.success) {
        setSuccess('Backup creado correctamente');
        await loadBackups();
        await loadStats();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(response.data.error || 'Error al crear el backup');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Error al crear el backup');
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteBackup = async (id) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este backup?')) {
      return;
    }

    try {
      await backupsAPI.delete(id);
      setSuccess('Backup eliminado correctamente');
      await loadBackups();
      await loadStats();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al eliminar el backup');
      console.error(err);
    }
  };

  const handleDownloadBackup = (id) => {
    backupsAPI.download(id);
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const pow = Math.floor(Math.log(bytes) / Math.log(1024));
    const pow2 = Math.min(pow, units.length - 1);
    return (bytes / Math.pow(1024, pow2)).toFixed(2) + ' ' + units[pow2];
  };

  if (loading) {
    return <div className="loading">Cargando backups...</div>;
  }

  return (
    <div className="backups">
      <div className="page-header">
        <h2>Gestión de Backups</h2>
        <button 
          className="btn btn-primary" 
          onClick={handleCreateBackup}
          disabled={creating}
        >
          {creating ? 'Creando...' : 'Crear Backup'}
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {stats && (
        <div className="backups-stats">
          <div className="stat-card">
            <div className="stat-label">Total de Backups</div>
            <div className="stat-value">{stats.total || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Espacio Total</div>
            <div className="stat-value">{formatBytes(stats.total_size || 0)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Último Backup</div>
            <div className="stat-value">
              {stats.last_backup 
                ? new Date(stats.last_backup).toLocaleDateString('es-ES', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })
                : 'Nunca'}
            </div>
          </div>
        </div>
      )}

      <div className="backups-list">
        <h3>Historial de Backups</h3>
        {backups.length === 0 ? (
          <div className="empty-state">
            <p>No hay backups disponibles. Crea tu primer backup ahora.</p>
          </div>
        ) : (
          <table className="backups-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Archivo</th>
                <th>Tamaño</th>
                <th>Creado por</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {backups.map(backup => (
                <tr key={backup.id}>
                  <td>
                    {backup.created_at_formatted || 
                     new Date(backup.created_at).toLocaleDateString('es-ES', {
                       day: '2-digit',
                       month: '2-digit',
                       year: 'numeric',
                       hour: '2-digit',
                       minute: '2-digit'
                     })}
                  </td>
                  <td>{backup.filename}</td>
                  <td>{backup.size_formatted || formatBytes(backup.size)}</td>
                  <td>
                    {backup.nombre && backup.apellidos
                      ? `${backup.nombre} ${backup.apellidos}`
                      : 'Sistema'}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleDownloadBackup(backup.id)}
                        title="Descargar"
                      >
                        📥 Descargar
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteBackup(backup.id)}
                        title="Eliminar"
                      >
                        🗑️ Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default Backups;

